import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Youtube from "@tiptap/extension-youtube";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImageIcon,
  Youtube as YoutubeIcon, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, LayoutPanelLeft,
} from "lucide-react";
import { toast } from "sonner";
import { linkPreview } from "@/lib/api";

// Image with alignment/wrap support (float left/right, centered)
const AlignImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        parseHTML: (el) => el.getAttribute("data-align") || "center",
        renderHTML: (attrs) => ({ "data-align": attrs.align, class: `img-${attrs.align}` }),
      },
    };
  },
});

const Btn = ({ active, onClick, title, children }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`flex h-8 w-8 items-center justify-center rounded transition-colors ${active ? "bg-navy text-white" : "text-navy hover:bg-navy/10"}`}>
    {children}
  </button>
);
const Sep = () => <span className="mx-1 h-5 w-px bg-border" />;

export function RichEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      AlignImage,
      Link.configure({ openOnClick: false, autolink: true }),
      Youtube.configure({ width: 640, height: 360, nocookie: true }),
      TextAlign.configure({ types: ["paragraph", "heading"] }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "tiptap prose-usapulse min-h-[320px] px-4 py-3 focus:outline-none" } },
  });

  const addImage = useCallback(() => {
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url, align: "center" }).run();
  }, [editor]);

  const setImgAlign = (align) => editor.chain().focus().updateAttributes("image", { align }).run();

  const addVideo = useCallback(() => {
    const url = window.prompt("YouTube URL");
    if (url) editor.commands.setYoutubeVideo({ src: url });
  }, [editor]);

  const addLink = useCallback(() => {
    const url = window.prompt("Link URL");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addLinkCard = useCallback(async () => {
    const url = window.prompt("Paste a URL to insert a link card");
    if (!url) return;
    try {
      const p = await linkPreview(url);
      const html =
        `<blockquote class="link-card"><p><strong>${escapeHtml(p.title)}</strong></p>` +
        (p.description ? `<p>${escapeHtml(p.description)}</p>` : "") +
        `<p><a href="${escapeHtml(p.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(p.url)}</a></p></blockquote><p></p>`;
      editor.chain().focus().insertContent(html).run();
    } catch {
      toast.error("Could not fetch that link's preview.");
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-white" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-parchment/50 px-2 py-1.5">
        <Btn title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Text left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></Btn>
        <Btn title="Text center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></Btn>
        <Btn title="Text right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Insert image" onClick={addImage}><ImageIcon className="h-4 w-4" /></Btn>
        {editor.isActive("image") && (
          <>
            <Btn title="Wrap left" onClick={() => setImgAlign("left")}><AlignLeft className="h-4 w-4 text-oldglory-red" /></Btn>
            <Btn title="Center" onClick={() => setImgAlign("center")}><AlignCenter className="h-4 w-4 text-oldglory-red" /></Btn>
            <Btn title="Wrap right" onClick={() => setImgAlign("right")}><AlignRight className="h-4 w-4 text-oldglory-red" /></Btn>
          </>
        )}
        <Btn title="Embed video" onClick={addVideo}><YoutubeIcon className="h-4 w-4" /></Btn>
        <Btn title="Add link" active={editor.isActive("link")} onClick={addLink}><Link2 className="h-4 w-4" /></Btn>
        <Btn title="Insert link card" onClick={addLinkCard}><LayoutPanelLeft className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></Btn>
        <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
